/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropertyKeyAvailableType } from '@acp/shared';
import { useProperty } from './use-property';
import { useUpdatePropertyDetailsMutation } from '../components/edit-property-modal/use-edit-property-details-mutation';
import { editIntegratedPropertyFormSchema, EditIntegratedPropertyFormValues } from '../components/edit-property-modal/integrated-form-schema';

export function useUpdateIntegratedPropertyForm({
    propertyId,
    onClose,
    onUpdatedProperty,
    isReadOnly = false,
}: {
    propertyId: number;
    onClose?: () => void;
    onUpdatedProperty?: () => void;
    isReadOnly?: boolean;
}) {
    const defaultScreen = isReadOnly ? 'READ_ONLY' : 'EDIT_PROPERTY_DATA';
    const [screen, setScreen] = useState<'EDIT_PROPERTY_DATA' | 'CONFIRM' | 'READ_ONLY'>(defaultScreen);

    const { data: propertyData } = useProperty(propertyId);

    const toast = useToast();

    const editPropertyForm = useForm<EditIntegratedPropertyFormValues>({
        resolver: zodResolver(editIntegratedPropertyFormSchema),
        defaultValues: {
            propertyId,
            propertyKey: {
                onsiteAvailability: propertyData?.propertyKeyInformation.onsiteAvailability
                    ?? PropertyKeyAvailableType.Unavailable,
                // eslint-disable-next-line max-len
                temporaryKeyExpirationDate: propertyData?.propertyKeyInformation.temporaryKeyExpirationDate != null
                    ? new Date(propertyData?.propertyKeyInformation.temporaryKeyExpirationDate)
                    : null,
                keyOnsiteNotes: propertyData?.propertyKeyInformation.keyOnsiteNotes,
            },
        },
        mode: 'onChange',
    });

    const handleClose = () => {
        editPropertyForm.reset();
        onClose?.();
        setScreen(isReadOnly ? 'READ_ONLY' : 'EDIT_PROPERTY_DATA');
    };

    const updatePropertyDetailsMutation = useUpdatePropertyDetailsMutation(propertyId, {
        onSuccess: () => {
            toast({
                status: 'success',
                title: 'Update property request submitted',
                description:
                    'Property details has been saved successfully',
            });

            // callback
            onUpdatedProperty?.();

            handleClose();
        },
    });

    const handleSubmit = () => {
        setScreen('CONFIRM');
    };

    const handleBack = () => {
        updatePropertyDetailsMutation.reset();
        setScreen('EDIT_PROPERTY_DATA');
    };

    const handleConfirm = (vals: EditIntegratedPropertyFormValues) => {
        updatePropertyDetailsMutation.mutate(vals);
    };

    return {
        screen,
        editPropertyForm,
        handleSubmit,
        handleBack,
        handleClose,
        handleConfirm,
        updatePropertyDetailsMutation,
    };
}
